import type { TTaskPageVariant } from "@components/modules/resource-planning/tasks";
import type {
  ClientRect,
  Collision,
  CollisionDetection,
  DroppableContainer,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { pointerWithin } from "@dnd-kit/core";
import type { TaskBoardGroupByEnum } from "@src/__generated__/graphql";
import type {
  BoardDragData,
  BoardDropPosition,
} from "@src/components/modules/resource-planning/tasks/kanban/types";
import { formatDuration } from "@src/utils/formatters";

export function makeBoardKey(
  tab: TTaskPageVariant,
  groupBy: TaskBoardGroupByEnum,
): string {
  return `${tab}:${groupBy}`;
}

/**
 * One task can sit in several assignee columns at once, so the card's
 * draggable id must include the column to stay unique in the DndContext.
 */
export function boardCardDragId(columnKey: string, taskId: string): string {
  return `${columnKey}::${taskId}`;
}

/**
 * Card drags use pointer position. A card under the pointer wins; otherwise
 * the card in that column closest to the pointer does, so the gaps between
 * cards and the space under the last one keep a target instead of falling
 * through to the column body. Only an empty column resolves to the column
 * itself. A card collision carries which half of the card the pointer is in.
 */
export const boardCollisionDetection: CollisionDetection = (args) => {
  const pointer = args.pointerCoordinates;
  const collisions = pointerWithin(args);
  if (collisions.length === 0 || !pointer) return collisions;

  const hits = collisions.flatMap((collision) => {
    const container = collision.data?.droppableContainer as
      | DroppableContainer
      | undefined;
    return container ? [container] : [];
  });

  const hitCard = hits.find(
    (container) => boardDragData(container)?.type === "card",
  );
  const columnKey = boardDragData(hitCard ?? hits[0])?.columnKey;

  let target: { container: DroppableContainer; rect: ClientRect } | null = null;
  let targetDistance = Number.POSITIVE_INFINITY;

  for (const container of hitCard ? [hitCard] : args.droppableContainers) {
    const data = boardDragData(container);
    if (data?.type !== "card" || data.columnKey !== columnKey) continue;

    const rect = args.droppableRects.get(container.id);
    if (!rect) continue;

    const distance = Math.max(rect.top - pointer.y, pointer.y - rect.bottom, 0);
    if (distance < targetDistance) {
      target = { container, rect };
      targetDistance = distance;
    }
  }

  if (!target) return [collisions[0]];

  const position: BoardDropPosition =
    pointer.y > target.rect.top + target.rect.height / 2 ? "after" : "before";

  return [
    {
      id: target.container.id,
      data: { droppableContainer: target.container, position },
    },
  ];
};

/** Which half of the target card the pointer is in, from the collision. */
export function boardCollisionDropPosition(
  collisions: Collision[] | null,
  overId: UniqueIdentifier,
): BoardDropPosition | null {
  const collision = collisions?.find((item) => item.id === overId);
  return (collision?.data?.position as BoardDropPosition | undefined) ?? null;
}

function boardDragData(
  container: DroppableContainer | undefined,
): BoardDragData | undefined {
  return container?.data.current as BoardDragData | undefined;
}

export function formatBoardHours(
  spentSeconds: number,
  plannedSeconds: number,
): string {
  return `${formatDuration(spentSeconds, { noPadStart: true })} / ${formatDuration(plannedSeconds, { noPadStart: true })}`;
}
